import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody } from '../components/Card.js'
import { Empty } from '../components/Empty.js'
import { Icon } from '../components/Icon.js'
import { Money } from '../components/Money.js'
import { StatusPill } from '../components/StatusPill.js'

export const Route = createFileRoute('/_app/children/')({
  component: ChildrenPage,
})

interface ChildRow {
  studentId: string
  fullName: string
  examBoard: string | null
  schoolName: string | null
  classId: string | null
  className: string | null
  academyId: string
  academyName: string
  academySlug: string
  academyLogoUrl: string | null
  academyPrimaryColor: string | null
  relationship: string | null
  isPrimary: boolean
}

interface ReferralPayload {
  shareCodes: {
    studentId: string
    studentName: string
    academyId: string
    academyName: string
    code: string
  }[]
  referrals: {
    id: string
    academyName: string
    referredStudentName: string | null
    discountApplied: boolean
    discountAmountInr: number | null
    createdAt: string
    appliedAt: string | null
  }[]
}

function ChildrenPage() {
  const children = useQuery({
    queryKey: ['me', 'children'],
    queryFn: async () => {
      const res = await api<ChildRow[]>('/api/v1/me/children')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const referral = useQuery({
    queryKey: ['me', 'referral'],
    queryFn: async () => {
      const res = await api<ReferralPayload>('/api/v1/me/referral')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  // Group by academy.
  const grouped = new Map<string, { academyName: string; rows: ChildRow[] }>()
  for (const c of children.data ?? []) {
    const existing = grouped.get(c.academyId)
    if (existing) {
      existing.rows.push(c)
    } else {
      grouped.set(c.academyId, { academyName: c.academyName, rows: [c] })
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Your children"
        description="All children linked to your account, grouped by academy."
      />

      {referral.data && referral.data.shareCodes.length > 0 ? (
        <ReferralCard
          shareCodes={referral.data.shareCodes}
          referrals={referral.data.referrals}
        />
      ) : null}

      {children.isLoading && <p className="text-sm text-fg-subtle">Loading…</p>}
      {children.error && (
        <p className="text-sm text-danger">
          {children.error instanceof Error
            ? children.error.message
            : 'Failed to load children'}
        </p>
      )}
      {children.data && children.data.length === 0 && (
        <Card>
          <Empty
            icon="bookOpen"
            title="No children linked yet"
            description="The academy admin will add you when your child is enrolled. You'll get a WhatsApp message once it's done."
          />
        </Card>
      )}

      {Array.from(grouped.entries()).map(([academyId, group]) => (
        <section key={academyId} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
            {group.academyName}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.rows.map((child) => (
              <Link
                key={child.studentId}
                to="/children/$id"
                params={{ id: child.studentId }}
                className="block group"
              >
                <Card className="transition-colors group-hover:border-border-strong group-hover:shadow-md">
                  <CardBody>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-primary-tint)] text-[var(--color-primary)] shrink-0">
                          <Icon name="bookOpen" size={18} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-base font-semibold text-fg truncate">
                            {child.fullName}
                          </p>
                          <p className="text-xs text-fg-subtle mt-0.5">
                            {child.relationship ?? '—'}
                            {child.isPrimary ? ' · primary contact' : ''}
                          </p>
                          <div className="mt-3 space-y-0.5 text-xs text-fg-muted">
                            {child.className ? <p>Class: {child.className}</p> : null}
                            {child.schoolName ? <p>School: {child.schoolName}</p> : null}
                            {child.examBoard ? (
                              <p className="uppercase">
                                Board: {child.examBoard.replace('_', ' ')}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <Icon
                        name="chevronRight"
                        size={16}
                        className="text-fg-faint group-hover:text-fg shrink-0 mt-1"
                      />
                    </div>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function ReferralCard({
  shareCodes,
  referrals,
}: {
  shareCodes: ReferralPayload['shareCodes']
  referrals: ReferralPayload['referrals']
}) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const onCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(null), 1500)
    } catch {
      // ignore
    }
  }

  const credited = referrals.filter((r) => r.discountApplied)
  const totalCreditPaise = credited.reduce(
    (sum, r) => sum + (r.discountAmountInr ?? 0),
    0,
  )

  return (
    <Card className="bg-[var(--color-primary-tint)] border-[var(--color-primary)]/20">
      <CardBody>
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface text-[var(--color-primary)] shrink-0">
            <Icon name="users" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">Refer a friend, get 5% off</p>
            <p className="mt-0.5 text-xs text-fg-muted">
              Share your child&apos;s referred-by code with another parent. They&apos;ll get
              5% off their first invoice when they enrol.
            </p>

            <div className="mt-3 space-y-2">
              {shareCodes.map((s) => {
                const copied = copiedCode === s.code
                return (
                  <div
                    key={s.studentId}
                    className="flex items-center justify-between gap-3 rounded-md bg-surface border border-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-fg-subtle truncate">
                        {s.studentName} · {s.academyName}
                      </p>
                      <p className="font-mono text-sm font-semibold text-fg tracking-wide">
                        {s.code}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onCopy(s.code)}
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                        copied
                          ? 'border-success-border bg-success-tint text-success'
                          : 'border-border bg-surface text-fg-muted hover:bg-surface-muted'
                      }`}
                    >
                      <Icon name={copied ? 'check' : 'fileText'} size={12} />
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )
              })}
            </div>

            {referrals.length > 0 ? (
              <div className="mt-4 border-t border-border pt-3">
                <div className="flex items-center justify-between text-xs">
                  <p className="text-fg-subtle">
                    {referrals.length} referral{referrals.length === 1 ? '' : 's'}
                    {credited.length > 0 ? ` · ${credited.length} credited` : ''}
                  </p>
                  {totalCreditPaise > 0 ? (
                    <span className="text-success">
                      <Money paise={totalCreditPaise} /> credited
                    </span>
                  ) : null}
                </div>
                <ul className="mt-2 divide-y divide-border">
                  {referrals.slice(0, 5).map((r) => (
                    <li
                      key={r.id}
                      className="py-2 flex items-center justify-between gap-2 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="text-fg truncate">
                          {r.referredStudentName ?? '(student removed)'}
                        </p>
                        <p className="text-fg-subtle">
                          {r.academyName} ·{' '}
                          {new Date(r.createdAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <StatusPill tone={r.discountApplied ? 'paid' : 'pending'}>
                        {r.discountApplied ? 'Credited' : 'Pending'}
                      </StatusPill>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
